/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
	$isCodeNode,
	CODE_LANGUAGE_FRIENDLY_NAME_MAP,
	CODE_LANGUAGE_MAP,
	getLanguageFriendlyName,
} from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $isListNode, ListNode } from "@lexical/list";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { $isHeadingNode } from "@lexical/rich-text";
import {
	$getSelectionStyleValueForProperty,
	$isParentElementRTL,
	$patchStyleText,
} from "@lexical/selection";
import { $isTableNode, $isTableSelection } from "@lexical/table";
import {
	$findMatchingParent,
	$getNearestNodeOfType,
	$isEditorIsNestedEditor,
	mergeRegister,
} from "@lexical/utils";
import {
	$getNodeByKey,
	$getSelection,
	$isElementNode,
	$isRangeSelection,
	$isRootOrShadowRoot,
	CAN_REDO_COMMAND,
	CAN_UNDO_COMMAND,
	COMMAND_PRIORITY_CRITICAL,
	ElementFormatType,
	FORMAT_ELEMENT_COMMAND,
	FORMAT_TEXT_COMMAND,
	HISTORIC_TAG,
	INDENT_CONTENT_COMMAND,
	LexicalEditor,
	NodeKey,
	OUTDENT_CONTENT_COMMAND,
	REDO_COMMAND,
	SELECTION_CHANGE_COMMAND,
	UNDO_COMMAND,
} from "lexical";
import { Dispatch, useCallback, useEffect, useState } from "react";
import { IS_APPLE } from "../../shared/environment";
import useModal from "../../hooks/useModal";
import DropDown, { DropDownItem } from "../../ui/DropDown";
import DropdownColorPicker from "../../ui/DropdownColorPicker";
import { getSelectedNode } from "../../utils/getSelectedNode";
import { sanitizeUrl } from "../../utils/url";
import FontSize from "./fontSize";
import { InsertImageDialog } from "../ImagesPlugin";
import {
	blockTypeToBlockName,
	useToolbarState,
} from "../../context/ToolbarContext";
import {
	clearFormatting,
	formatBulletList, formatHeading,
	formatNumberedList,
	formatParagraph,
	formatQuote
} from "./utils";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { TotalAttachmentSizeLimit, AttachmentSizeLimit } from "@/common/constants";

function getCodeLanguageOptions(): [string, string][] {
	const options: [string, string][] = [];

	for (const [lang, friendlyName] of Object.entries(
		CODE_LANGUAGE_FRIENDLY_NAME_MAP
	)) {
		options.push([lang, friendlyName]);
	}

	return options;
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions();

const FONT_FAMILY: string = "font-family";
const FONT_FAMILY_OPTIONS: [string, string][] = [
	["Arial", "Arial"],
	["Courier New", "Courier New"],
	["Georgia", "Georgia"],
	["Times New Roman", "Times New Roman"],
	["Trebuchet MS", "Trebuchet MS"],
	["Verdana", "Verdana"],
	["Calibri Light", "Calibri"],
];

const FONT_SIZE_OPTIONS: [string, string][] = [
	["10px", "10px"],
	["11px", "11px"],
	["12px", "12px"],
	["13px", "13px"],
	["14px", "14px"],
	["15px", "15px"],
	["16px", "16px"],
	["17px", "17px"],
	["18px", "18px"],
	["19px", "19px"],
	["20px", "20px"],
];
const DEFAULT_FONT_SIZE = '11';

const ELEMENT_FORMAT_OPTIONS: {
	[key in Exclude<ElementFormatType, "">]: {
		icon: string;
		iconRTL: string;
		name: string;
	};
} = {
	center: {
		icon: "center-align",
		iconRTL: "center-align",
		name: "Center Align",
	},
	end: {
		icon: "right-align",
		iconRTL: "left-align",
		name: "End Align",
	},
	justify: {
		icon: "justify-align",
		iconRTL: "justify-align",
		name: "Justify Align",
	},
	left: {
		icon: "left-align",
		iconRTL: "left-align",
		name: "Left Align",
	},
	right: {
		icon: "right-align",
		iconRTL: "right-align",
		name: "Right Align",
	},
	start: {
		icon: "left-align",
		iconRTL: "right-align",
		name: "Start Align",
	},
};

const TOOLBAR_ITEM_SPACED = "toolbar-item spaced";

function getDefaultFontFamily(appSettings: any): string {
    const fallbackFont = "Arial";
    const defaultFont = appSettings?.emailEditorDefaultFont;
    let fontToApply = FONT_FAMILY_OPTIONS.some(
        ([_, label]) => label === defaultFont
    )
        ? defaultFont
        : fallbackFont;

    if (fontToApply === "Calibri") {
        fontToApply = "Calibri Light";
    }

    return fontToApply;
}

function dropDownActiveClass(active: boolean) {
	if (active) {
		return "active dropdown-item-active";
	} else {
		return "";
	}
}

function BlockFormatDropDown({
	editor,
	blockType,
	disabled = false,
}: {
	blockType: keyof typeof blockTypeToBlockName;
	editor: LexicalEditor;
	disabled?: boolean;
}): JSX.Element {
	return (
		<DropDown
			disabled={disabled}
			buttonClassName="toolbar-item block-controls"
			buttonIconClassName={"icon block-type " + blockType}
			buttonLabel={blockTypeToBlockName[blockType]}
			buttonAriaLabel="Formatting options for text style"
		>
			<DropDownItem
				className={
					"item wide " + dropDownActiveClass(blockType === "extended-paragraph")
				}
				onClick={() => formatParagraph(editor)}
			>
				<div className="icon-text-container">
					<i className="icon extended-paragraph" />
					<span className="text">Normal</span>
				</div>
			</DropDownItem>
			<DropDownItem
				className={"item wide " + dropDownActiveClass(blockType === "h1")}
				onClick={() => formatHeading(editor, blockType, "h1")}
			>
				<div className="icon-text-container">
					<i className="icon h1" />
					<span className="text">Heading 1</span>
				</div>
			</DropDownItem>
			<DropDownItem
				className={"item wide " + dropDownActiveClass(blockType === "h2")}
				onClick={() => formatHeading(editor, blockType, "h2")}
			>
				<div className="icon-text-container">
					<i className="icon h2" />
					<span className="text">Heading 2</span>
				</div>
			</DropDownItem>
			<DropDownItem
				className={"item wide " + dropDownActiveClass(blockType === "h3")}
				onClick={() => formatHeading(editor, blockType, "h3")}
			>
				<div className="icon-text-container">
					<i className="icon h3" />
					<span className="text">Heading 3</span>
				</div>
			</DropDownItem>
			<DropDownItem
				className={"item wide " + dropDownActiveClass(blockType === "bullet")}
				onClick={() => formatBulletList(editor, blockType)}
			>
				<div className="icon-text-container">
					<i className="icon bullet-list" />
					<span className="text">Bullet List</span>
				</div>
			</DropDownItem>
			<DropDownItem
				className={"item wide " + dropDownActiveClass(blockType === "number")}
				onClick={() => formatNumberedList(editor, blockType)}
			>
				<div className="icon-text-container">
					<i className="icon numbered-list" />
					<span className="text">Numbered List</span>
				</div>
			</DropDownItem>
			<DropDownItem
				className={"item wide " + dropDownActiveClass(blockType === "quote")}
				onClick={() => formatQuote(editor, blockType)}
			>
				<div className="icon-text-container">
					<i className="icon quote" />
					<span className="text">Quote</span>
				</div>
			</DropDownItem>
		</DropDown>
	);
}

function Divider(): JSX.Element {
	return <div className="divider" />;
}

function FontDropDown({
	editor,
	value,
	style,
	disabled = false,
}: {
	editor: LexicalEditor;
	value: string;
	style: string;
	disabled?: boolean;
}): JSX.Element {
	const handleClick = useCallback(
		(option: string) => {
			editor.update(() => {
				const selection = $getSelection();
				if (selection !== null) {
					$patchStyleText(selection, {
						[style]: option,
					});
				}
			});
		},
		[editor, style]
	);

	const buttonAriaLabel =
		style === FONT_FAMILY
			? "Formatting options for font family"
			: "Formatting options for font size";

	return (
		<DropDown
			disabled={disabled}
			buttonClassName={"toolbar-item " + style}
			buttonLabel={value}
			buttonIconClassName={
				style === FONT_FAMILY ? "icon block-type font-family" : ""
			}
			buttonAriaLabel={buttonAriaLabel}
		>
			{(style === FONT_FAMILY ? FONT_FAMILY_OPTIONS : FONT_SIZE_OPTIONS).map(
				([option, text]) => (
					<DropDownItem
						className={`item ${dropDownActiveClass(value === option)} ${
							style === "font-size" ? "fontsize-item" : ""
						}`}
						onClick={() => handleClick(option)}
						key={option}
					>
						<span className="text">{text}</span>
					</DropDownItem>
				)
			)}
		</DropDown>
	);
}

function ElementFormatDropdown({
	editor,
	value,
	isRTL,
	disabled = false,
}: {
	editor: LexicalEditor;
	value: ElementFormatType;
	isRTL: boolean;
	disabled: boolean;
}) {
	const formatOption = ELEMENT_FORMAT_OPTIONS[value || "left"];

	return (
		<DropDown
			disabled={disabled}
			buttonLabel={formatOption.name}
			buttonIconClassName={`icon ${
				isRTL ? formatOption.iconRTL : formatOption.icon
			}`}
			buttonClassName={`${TOOLBAR_ITEM_SPACED} alignment`}
			buttonAriaLabel="Formatting options for text alignment"
		>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
				}}
				className="item wide"
			>
				<div className="icon-text-container">
					<i className="icon left-align" />
					<span className="text">Left Align</span>
				</div>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
				}}
				className="item wide"
			>
				<div className="icon-text-container">
					<i className="icon center-align" />
					<span className="text">Center Align</span>
				</div>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
				}}
				className="item wide"
			>
				<div className="icon-text-container">
					<i className="icon right-align" />
					<span className="text">Right Align</span>
				</div>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
				}}
				className="item wide"
			>
				<div className="icon-text-container">
					<i className="icon justify-align" />
					<span className="text">Justify Align</span>
				</div>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "start");
				}}
				className="item wide"
			>
				<i
					className={`icon ${
						isRTL
							? ELEMENT_FORMAT_OPTIONS.start.iconRTL
							: ELEMENT_FORMAT_OPTIONS.start.icon
					}`}
				/>
				<span className="text">Start Align</span>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "end");
				}}
				className="item wide"
			>
				<i
					className={`icon ${
						isRTL
							? ELEMENT_FORMAT_OPTIONS.end.iconRTL
							: ELEMENT_FORMAT_OPTIONS.end.icon
					}`}
				/>
				<span className="text">End Align</span>
			</DropDownItem>
			<Divider />
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
				}}
				className="item wide"
			>
				<div className="icon-text-container">
					<i className={"icon " + (isRTL ? "indent" : "outdent")} />
					<span className="text">Outdent</span>
				</div>
			</DropDownItem>
			<DropDownItem
				onClick={() => {
					editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
				}}
				className="item wide"
			>
				<div className="icon-text-container">
					<i className={"icon " + (isRTL ? "outdent" : "indent")} />
					<span className="text">Indent</span>
				</div>
			</DropDownItem>
		</DropDown>
	);
}

export default function ToolbarPlugin({
	editor,
	activeEditor,
	setActiveEditor,
	setIsLinkEditMode,
	isEmailMonitoring = false,
	currentAttachmentsSizeBytes = 0,
	maxTotalAttachmentsSizeBytes = TotalAttachmentSizeLimit,
	maxAttachmentSizeBytes = AttachmentSizeLimit,
}: {
	editor: LexicalEditor;
	activeEditor: LexicalEditor;
	setActiveEditor: Dispatch<LexicalEditor>;
	setIsLinkEditMode: Dispatch<boolean>;
	isEmailMonitoring: boolean;
	currentAttachmentsSizeBytes?: number;
	maxTotalAttachmentsSizeBytes?: number;
	maxAttachmentSizeBytes?: number;
}): JSX.Element {
	const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(
		null
	);
	const [modal, showModal] = useModal();
	const [isEditable, setIsEditable] = useState(true);
	const { toolbarState, updateToolbarState } = useToolbarState();
	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	useEffect(() => {
		editor.update(() => {
			const selection = $getSelection();
			const fontToApply  = getDefaultFontFamily(appSettings);

			if (selection) {
				applyStyleText({ "font-family": fontToApply  });
				applyStyleText({ "font-size": `${appSettings?.emailEditorDefaultFontSize ?? DEFAULT_FONT_SIZE}px` });
			}
		});
	}, [editor, appSettings?.emailEditorDefaultFont, appSettings?.emailEditorDefaultFontSize]);

	const $updateToolbar = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			if (activeEditor !== editor && $isEditorIsNestedEditor(activeEditor)) {
				const rootElement = activeEditor.getRootElement();
				updateToolbarState(
					"isImageCaption",
					!!rootElement?.parentElement?.classList.contains(
						"image-caption-container"
					)
				);
			} else {
				updateToolbarState("isImageCaption", false);
			}

			const anchorNode = selection.anchor.getNode();
			let element =
				anchorNode.getKey() === "root"
					? anchorNode
					: $findMatchingParent(anchorNode, (e) => {
							const parent = e.getParent();
							return parent !== null && $isRootOrShadowRoot(parent);
						});

			if (element === null) {
				element = anchorNode.getTopLevelElementOrThrow();
			}

			const elementKey = element.getKey();
			const elementDOM = activeEditor.getElementByKey(elementKey);

			updateToolbarState("isRTL", $isParentElementRTL(selection));

			// Update links
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			const isLink = $isLinkNode(parent) || $isLinkNode(node);
			updateToolbarState("isLink", isLink);

			const tableNode = $findMatchingParent(node, $isTableNode);
			if ($isTableNode(tableNode)) {
				updateToolbarState("rootType", "table");
			} else {
				updateToolbarState("rootType", "root");
			}

			if (elementDOM !== null) {
				setSelectedElementKey(elementKey);
				if ($isListNode(element)) {
					const parentList = $getNearestNodeOfType<ListNode>(
						anchorNode,
						ListNode
					);
					const type = parentList
						? parentList.getListType()
						: element.getListType();

					updateToolbarState("blockType", type);
				} else {
					const type = $isHeadingNode(element)
						? element.getTag()
						: element.getType();
					if (type in blockTypeToBlockName) {
						updateToolbarState(
							"blockType",
							type as keyof typeof blockTypeToBlockName
						);
					}
					if ($isCodeNode(element)) {
						const language =
							element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
						updateToolbarState(
							"codeLanguage",
							language ? CODE_LANGUAGE_MAP[language] || language : ""
						);
						return;
					}
				}
			}
			// Handle buttons
			updateToolbarState(
				"fontColor",
				$getSelectionStyleValueForProperty(selection, "color", "#000")
			);
			updateToolbarState(
				"bgColor",
				$getSelectionStyleValueForProperty(
					selection,
					"background-color",
					"#fff"
				)
			);

			updateToolbarState(
				"fontFamily",
				$getSelectionStyleValueForProperty(selection, FONT_FAMILY, getDefaultFontFamily(appSettings))
			);
			let matchingParent;
			if ($isLinkNode(parent)) {
				// If node is a link, we need to fetch the parent paragraph node to set format
				matchingParent = $findMatchingParent(
					node,
					(parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
				);
			}

			// If matchingParent is a valid node, pass it's format type
			updateToolbarState(
				"elementFormat",
				$isElementNode(matchingParent)
					? matchingParent.getFormatType()
					: $isElementNode(node)
						? node.getFormatType()
						: parent?.getFormatType() || "left"
			);
		}
		if ($isRangeSelection(selection) || $isTableSelection(selection)) {
			//   Update text format
			updateToolbarState("isBold", selection.hasFormat("bold"));
			updateToolbarState("isItalic", selection.hasFormat("italic"));
			updateToolbarState("isUnderline", selection.hasFormat("underline"));
			updateToolbarState(
				"isStrikethrough",
				selection.hasFormat("strikethrough")
			);
			updateToolbarState("isSubscript", selection.hasFormat("subscript"));
			updateToolbarState("isSuperscript", selection.hasFormat("superscript"));
			updateToolbarState("isHighlight", selection.hasFormat("highlight"));
			updateToolbarState("isCode", selection.hasFormat("code"));
			updateToolbarState(
				"fontSize",
				$getSelectionStyleValueForProperty(selection, "font-size", `${appSettings?.emailEditorDefaultFontSize ?? DEFAULT_FONT_SIZE}px`)
			);
			updateToolbarState("isLowercase", selection.hasFormat("lowercase"));
			updateToolbarState("isUppercase", selection.hasFormat("uppercase"));
			updateToolbarState("isCapitalize", selection.hasFormat("capitalize"));
		}
	}, [activeEditor, editor, updateToolbarState]);

	useEffect(() => {
		return editor.registerCommand(
			SELECTION_CHANGE_COMMAND,
			(_payload, newEditor) => {
				setActiveEditor(newEditor);
				$updateToolbar();
				return false;
			},
			COMMAND_PRIORITY_CRITICAL
		);
	}, [editor, $updateToolbar, setActiveEditor]);

	useEffect(() => {
		activeEditor.getEditorState().read(() => {
			$updateToolbar();
		});
	}, [activeEditor, $updateToolbar]);

	useEffect(() => {
		return mergeRegister(
			editor.registerEditableListener((editable) => {
				setIsEditable(editable);
			}),
			activeEditor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					$updateToolbar();
				});
			}),
			activeEditor.registerCommand<boolean>(
				CAN_UNDO_COMMAND,
				(payload) => {
					updateToolbarState("canUndo", payload);
					return false;
				},
				COMMAND_PRIORITY_CRITICAL
			),
			activeEditor.registerCommand<boolean>(
				CAN_REDO_COMMAND,
				(payload) => {
					updateToolbarState("canRedo", payload);
					return false;
				},
				COMMAND_PRIORITY_CRITICAL
			)
		);
	}, [$updateToolbar, activeEditor, editor, updateToolbarState]);

	const applyStyleText = useCallback(
		(styles: Record<string, string>, skipHistoryStack?: boolean) => {
			activeEditor.update(
				() => {
					const selection = $getSelection();
					if (selection !== null) {
						$patchStyleText(selection, styles);
					}
				},
				skipHistoryStack ? { tag: HISTORIC_TAG } : {}
			);
		},
		[activeEditor]
	);

	const onFontColorSelect = useCallback(
		(value: string, skipHistoryStack: boolean) => {
			applyStyleText({ color: value }, skipHistoryStack);
		},
		[applyStyleText]
	);

	const onBgColorSelect = useCallback(
		(value: string, skipHistoryStack: boolean) => {
			applyStyleText({ "background-color": value }, skipHistoryStack);
		},
		[applyStyleText]
	);

	const insertLink = useCallback(() => {
		if (!toolbarState.isLink) {
			setIsLinkEditMode(true);
			activeEditor.dispatchCommand(
				TOGGLE_LINK_COMMAND,
				sanitizeUrl("https://")
			);
		} else {
			setIsLinkEditMode(false);
			activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
		}
	}, [activeEditor, setIsLinkEditMode, toolbarState.isLink]);

	const onCodeLanguageSelect = useCallback(
		(value: string) => {
			activeEditor.update(() => {
				if (selectedElementKey !== null) {
					const node = $getNodeByKey(selectedElementKey);
					if ($isCodeNode(node)) {
						node.setLanguage(value);
					}
				}
			});
		},
		[activeEditor, selectedElementKey]
	);

	const canViewerSeeInsertDropdown = !toolbarState.isImageCaption;
	const canViewerSeeInsertCodeButton = false; //not working for now
	
	return (
		<div className="toolbar">
			<button
				disabled={!toolbarState.canUndo || !isEditable}
				onClick={() => {
					activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
				}}
				title={IS_APPLE ? "Undo (⌘Z)" : "Undo (Ctrl+Z)"}
				type="button"
				className={TOOLBAR_ITEM_SPACED}
				aria-label="Undo"
			>
				<i className="format undo" />
			</button>
			<button
				disabled={!toolbarState.canRedo || !isEditable}
				onClick={() => {
					activeEditor.dispatchCommand(REDO_COMMAND, undefined);
				}}
				title={IS_APPLE ? "Redo (⇧⌘Z)" : "Redo (Ctrl+Y)"}
				type="button"
				className="toolbar-item"
				aria-label="Redo"
			>
				<i className="format redo" />
			</button>
			<Divider />
			{toolbarState.blockType in blockTypeToBlockName &&
				activeEditor === editor && (
					<>
						<BlockFormatDropDown
							disabled={!isEditable}
							blockType={toolbarState.blockType}
							editor={activeEditor}
						/>
						<Divider />
					</>
				)}
			{toolbarState.blockType === "code" ? (
				<DropDown
					disabled={!isEditable}
					buttonClassName="toolbar-item code-language"
					buttonLabel={getLanguageFriendlyName(toolbarState.codeLanguage)}
					buttonAriaLabel="Select language"
				>
					{CODE_LANGUAGE_OPTIONS.map(([value, name]) => {
						return (
							<DropDownItem
								className={`item ${dropDownActiveClass(
									value === toolbarState.codeLanguage
								)}`}
								onClick={() => onCodeLanguageSelect(value)}
								key={value}
							>
								<span className="text">{name}</span>
							</DropDownItem>
						);
					})}
				</DropDown>
			) : (
				<>
					<FontDropDown
						disabled={!isEditable}
						style={FONT_FAMILY}
						value={toolbarState.fontFamily}
						editor={activeEditor}
					/>
					<Divider />
					{!isEmailMonitoring && 
						<>
						<FontSize
							selectionFontSize={toolbarState.fontSize.slice(0, -2)}
							editor={activeEditor}
							disabled={!isEditable}
						/>
						<Divider />
						</>
					}
					<button
						disabled={!isEditable}
						onClick={() => {
							activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
						}}
						className={
							`${TOOLBAR_ITEM_SPACED} ` + (toolbarState.isBold ? "active" : "")
						}
						type="button"
						aria-label={`Format text as bold`}
					>
						<i className="format bold" />
					</button>
					<button
						disabled={!isEditable}
						onClick={() => {
							activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
						}}
						className={
							`${TOOLBAR_ITEM_SPACED} ` + (toolbarState.isItalic ? "active" : "")
						}
						type="button"
						aria-label={`Format text as italics`}
					>
						<i className="format italic" />
					</button>
					<button
						disabled={!isEditable}
						onClick={() => {
							activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
						}}
						className={
							`${TOOLBAR_ITEM_SPACED} ` +
							(toolbarState.isUnderline ? "active" : "")
						}
						type="button"
						aria-label={`Format text to underlined`}
					>
						<i className="format underline" />
					</button>
					{canViewerSeeInsertCodeButton && (
						<button
							disabled={!isEditable}
							onClick={() => {
								activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
							}}
							className={
								`${TOOLBAR_ITEM_SPACED} ` + (toolbarState.isCode ? "active" : "")
							}
							type="button"
							aria-label="Insert code block"
						>
							<i className="format code" />
						</button>
					)}
					<button
						disabled={!isEditable}
						onClick={insertLink}
						className={
							`${TOOLBAR_ITEM_SPACED} ` + (toolbarState.isLink ? "active" : "")
						}
						aria-label="Insert link"
						type="button"
					>
						<i className="format link" />
					</button>
					<DropdownColorPicker
						disabled={!isEditable}
						buttonClassName="toolbar-item color-picker"
						buttonAriaLabel="Formatting text color"
						buttonIconClassName="icon font-color"
						color={toolbarState.fontColor}
						onChange={onFontColorSelect}
						title="text color"
					/>
					<DropdownColorPicker
						disabled={!isEditable}
						buttonClassName="toolbar-item color-picker"
						buttonAriaLabel="Formatting background color"
						buttonIconClassName="icon bg-color"
						color={toolbarState.bgColor}
						onChange={onBgColorSelect}
						title="bg color"
					/>
					<DropDown
						disabled={!isEditable}
						buttonClassName={TOOLBAR_ITEM_SPACED}
						buttonLabel=""
						buttonAriaLabel="Formatting options for additional text styles"
						buttonIconClassName="icon dropdown-more"
					>
						<DropDownItem
							onClick={() => {
								activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "lowercase");
							}}
							className={
								"item wide " + dropDownActiveClass(toolbarState.isLowercase)
							}
							title="Lowercase"
							aria-label="Format text to lowercase"
						>
							<div className="icon-text-container">
								<i className="icon lowercase" />
								<span className="text">Lowercase</span>
							</div>
						</DropDownItem>
						<DropDownItem
							onClick={() => {
								activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "uppercase");
							}}
							className={
								"item wide " + dropDownActiveClass(toolbarState.isUppercase)
							}
							title="Uppercase"
							aria-label="Format text to uppercase"
						>
							<div className="icon-text-container">
								<i className="icon uppercase" />
								<span className="text">Uppercase</span>
							</div>
						</DropDownItem>
						<DropDownItem
							onClick={() => {
								activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "capitalize");
							}}
							className={
								"item wide " + dropDownActiveClass(toolbarState.isCapitalize)
							}
							title="Capitalize"
							aria-label="Format text to capitalize"
						>
							<div className="icon-text-container">
								<i className="icon capitalize" />
								<span className="text">Capitalize</span>
							</div>
						</DropDownItem>
						<DropDownItem
							onClick={() => {
								activeEditor.dispatchCommand(
									FORMAT_TEXT_COMMAND,
									"strikethrough"
								);
							}}
							className={
								"item wide " + dropDownActiveClass(toolbarState.isStrikethrough)
							}
							title="Strikethrough"
							aria-label="Format text with a strikethrough"
						>
							<div className="icon-text-container">
								<i className="icon strikethrough" />
								<span className="text">Strikethrough</span>
							</div>
						</DropDownItem>
						<DropDownItem
							onClick={() => {
								activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "subscript");
							}}
							className={
								"item wide " + dropDownActiveClass(toolbarState.isSubscript)
							}
							title="Subscript"
							aria-label="Format text with a subscript"
						>
							<div className="icon-text-container">
								<i className="icon subscript" />
								<span className="text">Subscript</span>
							</div>
						</DropDownItem>
						<DropDownItem
							onClick={() => {
								activeEditor.dispatchCommand(
									FORMAT_TEXT_COMMAND,
									"superscript"
								);
							}}
							className={
								"item wide " + dropDownActiveClass(toolbarState.isSuperscript)
							}
							title="Superscript"
							aria-label="Format text with a superscript"
						>
							<div className="icon-text-container">
								<i className="icon superscript" />
								<span className="text">Superscript</span>
							</div>
						</DropDownItem>
						<DropDownItem
							onClick={() => {
								activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "highlight");
							}}
							className={
								"item wide " + dropDownActiveClass(toolbarState.isHighlight)
							}
							title="Highlight"
							aria-label="Format text with a highlight"
						>
							<div className="icon-text-container">
								<i className="icon highlight" />
								<span className="text">Highlight</span>
							</div>
						</DropDownItem>
						<DropDownItem
							onClick={() => clearFormatting(activeEditor)}
							className="item wide"
							title="Clear text formatting"
							aria-label="Clear all text formatting"
						>
							<div className="icon-text-container">
								<i className="icon clear" />
								<span className="text">Clear Formatting</span>
							</div>
						</DropDownItem>
					</DropDown>
					{canViewerSeeInsertDropdown && (
						<>
							<Divider />
							<DropDown
								disabled={!isEditable}
								buttonClassName={TOOLBAR_ITEM_SPACED}
								buttonLabel="Insert"
								buttonAriaLabel="Insert specialized editor node"
								buttonIconClassName="icon plus"
							>
								<DropDownItem
									onClick={() => {
										activeEditor.dispatchCommand(
											INSERT_HORIZONTAL_RULE_COMMAND,
											undefined
										);
									}}
									className="item"
								>
									<i className="icon horizontal-rule" />
									<span className="text">Horizontal Rule</span>
								</DropDownItem>
								<DropDownItem
									onClick={() => {
										showModal("Insert Image", (onClose) => (
											<InsertImageDialog
												activeEditor={activeEditor}
												onClose={onClose}
												currentAttachmentsSizeBytes={currentAttachmentsSizeBytes}
												maxTotalAttachmentsSizeBytes={maxTotalAttachmentsSizeBytes}
												maxAttachmentSizeBytes={maxAttachmentSizeBytes}
											/>
										));
									}}
									className="item"
									disabled={currentAttachmentsSizeBytes >= maxTotalAttachmentsSizeBytes}
								>
									<i className="icon image" />
									<span className="text">Image</span>
								</DropDownItem>
							</DropDown>
						</>
					)}
				</>
			)}
			<Divider />
			<ElementFormatDropdown
				disabled={!isEditable}
				value={toolbarState.elementFormat}
				editor={activeEditor}
				isRTL={toolbarState.isRTL}
			/>

			{modal}
		</div>
	);
}
